import { useAuthState } from '@/app/providers/auth'
import { useLanguage } from '@/components/Link'
import type { BasketFragment } from '@/generated/graphql'
import { getCustomerFacingPriceItem } from '@/helpers/customerPricing/customerPricing'

import { parseISO, isValid as isValidDate } from 'date-fns'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import { validateUserBasketSettings } from '../../../common/domain/user-basket-settings'
import { useUserBasketSettingsState } from '../../../common/states/useUserBasketSettingsState'
import type { MenuItem } from '../../domain/menu-item'
import type { MenuVendor } from '../../domain/menu-vendor'
import { useMixpanelBasketCreated } from '../../hooks/useMixpanelBasketCreated'
import {
  type ItemAddSource,
  useMixpanelItemAddedToBasket,
} from '../../hooks/useMixpanelItemAddedToBasket'
import { useBasketSelection } from '../../states/basket-selection/useBasketSelection'
import type { AddItemPayload } from '../../states/menu-page/add-item-mutation'
import { useSelectedBasketState } from '../../states/selected-basket'
import { useExistingOrder } from '../../states/useExistingOrder'
import { useBasketStateStore } from '../../store/basketStateStore'
import { useCartModalActions } from '../../store/cartStore'
import { useVendorLocationData } from '../../store/vendorLocationStore'

export const useMenuItemCard = (
  menuItem: MenuItem,
  vendor: MenuVendor,
  { addSource = 'SupplierMenu' }: { addSource?: ItemAddSource } = {},
) => {
  const searchParams = useSearchParams()
  const language = useLanguage()
  const authState = useAuthState()
  const { t } = useTranslation()
  const { trackBasketCreated } = useMixpanelBasketCreated()

  const orderIdParam = searchParams?.get('orderId') || ''
  const basketIdParam = searchParams?.get('id') || searchParams?.get('basketId') || ''
  const standingOrderIdParam = searchParams?.get('standingOrderId') || ''
  const preventAutomaticBasketSelection = Boolean(searchParams?.get('preventAutoBasketSelection'))

  const { setOrderDetailsModalState } = useCartModalActions()

  const isEditingExistingOrder = Boolean(orderIdParam)
  const isEditingStandingOrder = Boolean(standingOrderIdParam)

  const account = useMemo(
    () =>
      match(authState)
        .with({ type: 'authenticated' }, ({ user }) => user.account)
        .otherwise(() => null),
    [authState],
  )

  const minQty = useMemo(() => {
    return menuItem.minQty && menuItem.minQty > 0 ? menuItem.minQty : 1
  }, [menuItem.minQty])

  const [quantity, setQuantity] = useState<number>(minQty)

  useEffect(() => {
    setQuantity((prev) => (prev < minQty ? minQty : prev))
  }, [minQty])

  const { servesAmount, price, priceCaveat } = useMemo(() => {
    let serves = `${menuItem.servings}`
    let computedPrice = menuItem.priceExTax
    let caveat = ''

    if (menuItem.isPricePerHead) {
      serves = `${menuItem.minQty}${menuItem.minQty > 1 ? '+' : ''}`
      if (menuItem.minQty > 1) {
        computedPrice = computedPrice * menuItem.minQty
        caveat = `${t('marketplace:for', { minQty: menuItem.minQty })}`
      }
    }

    return { servesAmount: serves, price: computedPrice, priceCaveat: caveat }
  }, [menuItem.isPricePerHead, menuItem.minQty, menuItem.priceExTax, menuItem.servings, t])

  const { selectedVendorLocation, deliveryFee, minOrderValueExTax } = useVendorLocationData()

  const { set: setUserBasketSettings, value: userBasketSettingsValue } =
    useUserBasketSettingsState()

  const { selectedBasket, createBasket } = useBasketSelection(
    {
      isAuthenticatedUser: authState.type === 'authenticated',
      accountId:
        authState.type === 'authenticated' ? (authState.user.account?.id ?? undefined) : undefined,
      vendorId:
        authState.type !== 'loading' && !isEditingExistingOrder && !isEditingStandingOrder
          ? vendor.id
          : undefined,
      initialSelectedId: basketIdParam,
      preventAutomaticSelection: preventAutomaticBasketSelection,
    },
    { syncConfig: false },
  )

  const existingOrderState = useExistingOrder()

  const { basket: basketData, addItemToBasket } = useSelectedBasketState({
    basketId: selectedBasket?.id ?? null,
    language,
    standingOrderId: standingOrderIdParam,
    deliveryFee,
    minOrderValueExTax,
    onBasketDataChange: useCallback(
      (newBasketData: BasketFragment) => {
        if (isEditingExistingOrder || isEditingStandingOrder) return
        const d = parseISO(newBasketData.settings.deliveryDate)
        const dateMs = isValidDate(d) ? d.getTime() : undefined
        setUserBasketSettings({
          location: newBasketData.settings.location || undefined,
          date: dateMs,
          time: newBasketData.settings.deliveryTimeSlot,
          headCount: newBasketData.settings.headCount,
        })
      },
      [isEditingExistingOrder, isEditingStandingOrder, setUserBasketSettings],
    ),
  })

  // Use shared loading state from the store
  const { addingItem: sharedAddingItem } = useBasketStateStore()

  const { trackItemAddedToBasket } = useMixpanelItemAddedToBasket({
    vendor,
    orderSettings: userBasketSettingsValue,
  })

  const trackAdd = useCallback(
    (payload: AddItemPayload) => {
      if (!payload.item) return
      trackItemAddedToBasket({
        itemId: payload.item._id,
        itemName: payload.item.name,
        quantity: payload.qty,
        source: addSource,
        isCateringTeamOrder: basketData?.isCateringTeamOrder,
      })
    },
    [trackItemAddedToBasket, addSource, basketData?.isCateringTeamOrder],
  )

  const handleQuantityIncrease = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setQuantity((prev) => prev + 1)
  }, [])

  const handleQuantityDecrease = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setQuantity((prev) => Math.max(0, prev - 1))
  }, [])

  // Create basket (optionally immediately add an item)
  const handleCreateBasket = useCallback(
    (itemPayload?: AddItemPayload) => {
      const availabilityId = vendor.gmAvailability?.id ?? null
      const vendorLocationId = selectedVendorLocation?.id ?? null
      const vendorLocationValid = Boolean(selectedVendorLocation)
      const validatedBasketSettings = validateUserBasketSettings(userBasketSettingsValue)

      // If any prerequisites are missing, open modal to collect them
      if (!vendorLocationValid || !availabilityId) {
        setOrderDetailsModalState({
          open: true,
          createOnConfirm: true,
          itemToAddOnCreate: itemPayload,
          itemToAddSource: addSource,
        })
        return
      }

      if (basketData && itemPayload) {
        // We already have a basket; just add.
        addItemToBasket(basketData, itemPayload)
        trackAdd(itemPayload)
        return
      }

      if (validatedBasketSettings.type === 'success') {
        createBasket({
          vendor,
          vendorLocationId,
          account,
          availabilityId,
          itemPayload,
          userBasketSettings: validatedBasketSettings.value,
          onBasketCreated: () => {
            trackBasketCreated({
              vendor: {
                id: vendor.id,
                companyName: vendor.companyName,
                isPantry: vendor.isPantry,
              },
              orderSettings: userBasketSettingsValue,
            })
            if (itemPayload) trackAdd(itemPayload)
          },
        })
        return
      }

      // Missing or invalid basket settings → open modal
      setOrderDetailsModalState({
        open: true,
        createOnConfirm: true,
        itemToAddOnCreate: itemPayload,
        itemToAddSource: addSource,
      })
    },
    [
      vendor,
      selectedVendorLocation,
      userBasketSettingsValue,
      basketData,
      setOrderDetailsModalState,
      addItemToBasket,
      createBasket,
      account,
      trackBasketCreated,
      trackAdd,
      addSource,
    ],
  )

  // Add item to basket or order (edit-mode aware)
  const handleAddItemToBasket = useCallback(
    (payload: AddItemPayload) => {
      if (isEditingExistingOrder) {
        const live = payload.item
        if (!live) return

        const customerPriceItem = getCustomerFacingPriceItem(live.customerPriceItems)

        existingOrderState.addNewItemToOrder({
          liveItemId: live._id,
          variationId: live.variationId,
          name: live.name,
          priceExTax: customerPriceItem.priceExTax,
          priceIncTax: customerPriceItem.price,
          qty: payload.qty,
          taxRate: live.taxRate,
          note: payload.note ?? undefined,
          minimumQuantity: live.minQty ?? 1,
          options:
            payload.options?.map((opt) => {
              const customerPriceItemOption = getCustomerFacingPriceItem(
                opt.optionItem.customerPriceItems,
              )
              return {
                id: opt.optionItem._id,
                name: opt.optionItem.item.name,
                optionCategoryId: opt.option._id,
                priceExTax: customerPriceItemOption.priceExTax,
                priceIncTax: customerPriceItemOption.price,
                qty: opt.qty * payload.qty,
                taxRate: opt.optionItem.taxRate,
                optionType: opt.option.optionType || '',
              }
            }) ?? [],
        })
        return
      }

      // Normal flow
      if (basketData) {
        addItemToBasket(basketData, payload)
        trackAdd(payload)
      } else {
        handleCreateBasket(payload)
      }
    },
    [
      isEditingExistingOrder,
      basketData,
      existingOrderState,
      addItemToBasket,
      handleCreateBasket,
      trackAdd,
    ],
  )

  // Quick add
  const handleAddItem = useCallback(() => {
    if (quantity <= 0) return
    handleAddItemToBasket({ item: menuItem, qty: quantity })
    setQuantity(minQty)
  }, [quantity, handleAddItemToBasket, menuItem, minQty])

  const handleAddToCart = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      handleAddItem()
    },
    [handleAddItem],
  )

  const quickAddDisabled = quantity <= minQty - 1

  return {
    quantity,
    account,
    minimumQuantity: minQty,

    servesAmount,
    price,
    priceCaveat,
    quickAddDisabled,

    handleQuantityIncrease,
    handleQuantityDecrease,
    handleAddItemToBasket,
    handleAddToCart,
    addingItem: sharedAddingItem,
  }
}
